import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
  GENERATE_MOVEMENT_EXECUTION_STATE_KEY,
  MovementExecutionStatusResponse,
  MovementGenRulesService,
  PersistedMovementExecutionState
} from '@app/services/model/reports/movement-gen-rules.service';
import { NotificationService } from '@app/_services/notification.service';

@Injectable({
  providedIn: 'root'
})
export class GenerateMovementExecutionTrackerService {
  private pollSub?: Subscription;
  private isChecking = false;

  constructor(
    private readonly movementGenRulesService: MovementGenRulesService,
    private readonly notificationService: NotificationService,
    private readonly router: Router
  ) { }

  startTracking(): void {
    if (this.pollSub) {
      return;
    }

    this.pollSub = interval(3000).subscribe(() => {
      this.checkRunningExecution();
    });

    // Immediate first check so users get completion notifications quickly on page reload.
    this.checkRunningExecution();
  }

  private checkRunningExecution(): void {
    const state = this.readExecutionState();
    if (!state || this.isChecking) {
      return;
    }

    this.isChecking = true;
    this.movementGenRulesService.getMovementGenerationExecutionStatus(state.statusQueryGetUri)
      .pipe(finalize(() => {
        this.isChecking = false;
      }))
      .subscribe((statusResponse) => {
        this.processStatusResponse(state, statusResponse);
      });
  }

  private processStatusResponse(
    state: PersistedMovementExecutionState,
    statusResponse: MovementExecutionStatusResponse | null
  ): void {
    const runtimeStatus = (statusResponse?.runtimeStatus ?? '').toLowerCase();

    if (runtimeStatus === 'completed') {
      this.clearExecutionState();
      if (!this.isOnGenerateMovementScreen()) {
        const output = statusResponse?.output;
        if (output?.success) {
          this.notificationService.showSuccess(output.message || 'Movement generation completed successfully.');
        } else if (output && output.success === false) {
          this.notificationService.showInfo(output.message || 'Movement generation completed with no new movements.');
        } else {
          this.notificationService.showInfo('Movement generation completed.');
        }
      }
      return;
    }

    if (runtimeStatus === 'failed' || runtimeStatus === 'terminated' || runtimeStatus === 'canceled') {
      this.clearExecutionState();
      if (!this.isOnGenerateMovementScreen()) {
        this.notificationService.showError('Movement generation failed. Please review logs and retry.');
      }
    }
  }

  private isOnGenerateMovementScreen(): boolean {
    const url = this.router.url || '';
    return url.includes('/report/generate-movement') || url.includes('/report/execute-generate-movement');
  }

  private readExecutionState(): PersistedMovementExecutionState | null {
    const raw = localStorage.getItem(GENERATE_MOVEMENT_EXECUTION_STATE_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as PersistedMovementExecutionState;
      if (!parsed?.planId || !parsed?.region || !parsed?.statusQueryGetUri || !parsed?.executionId) {
        this.clearExecutionState();
        return null;
      }

      return parsed;
    } catch {
      this.clearExecutionState();
      return null;
    }
  }

  private clearExecutionState(): void {
    localStorage.removeItem(GENERATE_MOVEMENT_EXECUTION_STATE_KEY);
  }
}
